import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Menu, Plus } from 'lucide-react';
import { useActiveTabId, useAppStore, useTabOrder, useTabTitle, type TabId } from './store.js';
import { ConfirmDialog } from './filter-modal-kit.js';

// SaxoTrader-style inline tab strip — lives in the top bar between the
// brand mark and the search trigger. Each tab is a clickable workspace
// switcher; the active tab carries its own ≡ menu (Rename / Save as new
// / Delete) and the inactive tabs are dim.
//
// Replaces the separate TabBar that used to sit below the top bar and
// the WorkspaceCluster (workspace dropdown + Save as + Delete) that
// used to sit in the top bar's right region. The hamburger menu makes
// both redundant.

export function TopBarTabs() {
  const { t } = useTranslation();
  const tabOrder = useTabOrder();
  const activeTabId = useActiveTabId();
  const addTab = useAppStore((s) => s.actions.addTab);
  const setActiveTab = useAppStore((s) => s.actions.setActiveTab);
  const reorderTab = useAppStore((s) => s.actions.reorderTab);

  // Roving tabindex: exactly one tab is in the page's tab order at a time, and
  // the arrow keys move between them. Without this the whole strip was
  // tabIndex=-1 — reachable only by mouse.
  //
  // MANUAL activation (arrows move focus, Enter/Space switches) rather than the
  // auto-activate variant: selecting a tab remounts the entire dockview canvas,
  // which is far too expensive to fire on every arrow press.
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedTabId, setFocusedTabId] = useState<TabId | null>(null);
  const rovingId =
    focusedTabId && tabOrder.includes(focusedTabId) ? focusedTabId : (activeTabId ?? tabOrder[0]);

  function focusTab(id: TabId) {
    setFocusedTabId(id);
    containerRef.current?.querySelector<HTMLElement>(`[data-workspace-tab="${id}"]`)?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!rovingId) return;
    const idx = tabOrder.indexOf(rovingId);
    if (idx < 0) return;
    const last = tabOrder.length - 1;
    let next: TabId | undefined;
    switch (e.key) {
      case 'ArrowRight':
        next = tabOrder[idx === last ? 0 : idx + 1];
        break;
      case 'ArrowLeft':
        next = tabOrder[idx === 0 ? last : idx - 1];
        break;
      case 'Home':
        next = tabOrder[0];
        break;
      case 'End':
        next = tabOrder[last];
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        setActiveTab(rovingId);
        return;
      default:
        return;
    }
    e.preventDefault();
    if (next) focusTab(next);
  }

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={t('tabBar.aria')}
      onKeyDown={onKeyDown}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
        marginRight: 'var(--s-3)',
      }}
    >
      {tabOrder.map((tabId) => (
        <TabItem
          key={tabId}
          tabId={tabId}
          isActive={tabId === activeTabId}
          isRoving={tabId === rovingId}
          canClose={tabOrder.length > 1}
          onActivate={setActiveTab}
          onReorder={reorderTab}
          onFocusTab={setFocusedTabId}
        />
      ))}
      <button
        type="button"
        aria-label={t('tabBar.newTab')}
        title={t('tabBar.newTab')}
        onClick={() => setActiveTab(addTab({ title: t('tabBar.newTabTitle') }))}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: 'var(--text-tertiary)',
          cursor: 'pointer',
          marginLeft: 6,
        }}
      >
        <Plus size={16} />
      </button>
    </div>
  );
}

// Custom drag type so the tab strip only accepts its own drags (not stray
// text / entity drops). dataTransfer carries the dragged tab id.
const TAB_DND = 'application/x-hc-tabid';

function TabItem({
  tabId,
  isActive,
  isRoving,
  canClose,
  onActivate,
  onReorder,
  onFocusTab,
}: {
  tabId: TabId;
  isActive: boolean;
  /** True for the one tab currently in the page tab order (roving tabindex). */
  isRoving: boolean;
  canClose: boolean;
  onActivate: (id: TabId) => void;
  onReorder: (draggedId: TabId, targetId: TabId, placeAfter?: boolean) => void;
  onFocusTab: (id: TabId) => void;
}) {
  const { t } = useTranslation();
  const title = useTabTitle(tabId) ?? '';
  const setTabTitle = useAppStore((s) => s.actions.setTabTitle);
  const removeTab = useAppStore((s) => s.actions.removeTab);
  const addTab = useAppStore((s) => s.actions.addTab);
  const resetTabLayout = useAppStore((s) => s.actions.resetTabLayout);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  // Insertion line on this tab's leading ('before') or trailing ('after') edge
  // during a drag-over — side chosen by the cursor relative to the midpoint.
  const [dropHint, setDropHint] = useState<'before' | 'after' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sideFor = (e: React.DragEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientX < r.left + r.width / 2 ? 'before' : 'after';
  };

  useEffect(() => {
    if (editing) {
      setDraft(title);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing, title]);

  function commit() {
    setTabTitle(tabId, draft);
    setEditing(false);
  }
  function cancel() {
    setDraft(title);
    setEditing(false);
  }

  function duplicate() {
    addTab({ title: `${title} copy` });
  }
  // Destructive to the arrangement (not to data), and a tab can hold dozens of
  // panels by the time anyone reaches for this — confirm first.
  const [confirmingReset, setConfirmingReset] = useState(false);
  function deleteTab() {
    if (!canClose) return;
    removeTab(tabId);
  }

  return (
    <div
      role="tab"
      aria-selected={isActive}
      // Not `data-tab-id` — the TabCanvas wrapper in shell.tsx already uses
      // that, and an unscoped lookup would match the canvas too.
      data-workspace-tab={tabId}
      tabIndex={isRoving && !editing ? 0 : -1}
      onFocus={() => onFocusTab(tabId)}
      draggable={!editing}
      // Clicks inside the ≡ dropdown (a portal) bubble through React's
      // synthetic tree to this handler — only activate on clicks that
      // originate from this element's real DOM subtree.
      onClick={(e) => !editing && e.currentTarget.contains(e.target as Node) && onActivate(tabId)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      onDragStart={(e) => {
        e.dataTransfer.setData(TAB_DND, tabId);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(TAB_DND)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const side = sideFor(e);
        if (dropHint !== side) setDropHint(side);
      }}
      onDragLeave={() => setDropHint(null)}
      onDrop={(e) => {
        const draggedId = e.dataTransfer.getData(TAB_DND) as TabId;
        const side = sideFor(e);
        setDropHint(null);
        if (draggedId) {
          e.preventDefault();
          onReorder(draggedId, tabId, side === 'after');
        }
      }}
      onDragEnd={() => setDropHint(null)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 14px',
        background: 'transparent',
        border: 'none',
        color: isActive ? 'var(--text-primary)' : 'var(--text-tertiary)',
        fontWeight: isActive ? 600 : 500,
        fontSize: 'var(--t-section)',
        letterSpacing: '-0.005em',
        cursor: editing ? 'text' : 'grab',
        borderRadius: 3,
        userSelect: 'none',
        // insertion line on the side the dragged tab will land
        boxShadow:
          dropHint === 'before'
            ? 'inset 2px 0 0 0 var(--accent)'
            : dropHint === 'after'
              ? 'inset -2px 0 0 0 var(--accent)'
              : 'none',
      }}
      onMouseOver={(e) => {
        if (!isActive) e.currentTarget.style.color = 'var(--text-secondary)';
      }}
      onMouseOut={(e) => {
        if (!isActive) e.currentTarget.style.color = 'var(--text-tertiary)';
      }}
    >
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          aria-label={t('tabBar.renameTab')}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
            e.stopPropagation();
          }}
          style={{
            font: 'inherit',
            color: 'inherit',
            background: 'transparent',
            border: 'none',
            outline: '1px solid var(--accent)',
            outlineOffset: 1,
            padding: 0,
            width: `${Math.max(draft.length, 4) + 1}ch`,
          }}
        />
      ) : (
        <span>{title}</span>
      )}
      {isActive && !editing && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={t('tabBar.tabMenuAria', { name: title })}
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: 'var(--accent)',
                cursor: 'pointer',
                lineHeight: 0,
              }}
            >
              <Menu size={14} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={6}
              collisionPadding={8}
              style={{
                minWidth: 160,
                background: 'var(--surface)',
                border: '1px solid var(--hairline)',
                borderRadius: 4,
                boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
                padding: 4,
                fontSize: 'var(--t-body)',
                zIndex: 9000,
              }}
            >
              <DropdownMenu.Item onSelect={() => setEditing(true)} style={menuRow()}>
                {t('tabBar.rename')}
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={duplicate} style={menuRow()}>
                {t('tabBar.duplicate')}
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={() => setConfirmingReset(true)} style={menuRow()}>
                {t('tabBar.resetLayout')}
              </DropdownMenu.Item>
              {canClose && (
                <>
                  <DropdownMenu.Separator
                    style={{ height: 1, background: 'var(--hairline)', margin: '4px 0' }}
                  />
                  <DropdownMenu.Item
                    onSelect={deleteTab}
                    style={{ ...menuRow(), color: 'var(--color-negative-500)' }}
                  >
                    {t('tabBar.delete')}
                  </DropdownMenu.Item>
                </>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
      <ConfirmDialog
        open={confirmingReset}
        onOpenChange={setConfirmingReset}
        title={t('tabBar.resetLayout')}
        body={t('tabBar.resetLayoutConfirm', { name: title })}
        confirmLabel={t('tabBar.resetLayout')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => resetTabLayout(tabId)}
      />
    </div>
  );
}

function menuRow(): React.CSSProperties {
  return {
    display: 'block',
    padding: '6px 10px',
    cursor: 'pointer',
    outline: 'none',
    borderRadius: 2,
    color: 'var(--text-primary)',
    fontSize: 'var(--t-body)',
  };
}
